import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import type { UseQueryResult } from '@tanstack/react-query';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { formatCivilDate } from '@eis/core';
import { EmptyState } from '@eis/patterns';
import { roleNameOf, type PartnerRoleRule } from '../model/partners';

/**
 * "Partnerverlauf" — who filled which role, and when (SDD-011 P-7).
 *
 * DERIVED from the timeline by the service: consecutive periods with the same partner in the same
 * role are one row, a gap or another partner starts a new one. Nothing here is stored, so a
 * correction re-derives it — which is why the panel sits beside the timeline rather than being a
 * table of its own.
 */
export function PartnerHistoryPanel({
  history,
  rules,
  locale,
}: {
  readonly history: UseQueryResult<{ readonly periods?: readonly InstanceApi.PartnerPeriod[] }>;
  readonly rules: readonly PartnerRoleRule[] | undefined;
  readonly locale: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const periods = history.data?.periods ?? [];
  const civil = (value: string | undefined, open: string): string =>
    value === undefined || value === '' ? open : formatCivilDate(value, locale);

  return (
    <Paper
      variant="outlined"
      component="section"
      aria-labelledby="partner-history"
      sx={{ p: 2.5 }}
      data-testid="partner-history"
    >
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="h3" component="h2" id="partner-history">
          {t('policies.partners.history.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('policies.partners.history.description')}
        </Typography>
      </Box>
      {history.isPending ? (
        <Skeleton variant="rounded" height={120} />
      ) : periods.length === 0 ? (
        <EmptyState compact description={t('policies.partners.history.empty')} />
      ) : (
        <Table size="small" aria-labelledby="partner-history">
          <TableHead>
            <TableRow>
              <TableCell>{t('policies.partners.history.role')}</TableCell>
              <TableCell>{t('policies.partners.history.partner')}</TableCell>
              <TableCell>{t('policies.partners.history.period')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {periods.map((period) => (
              <TableRow
                key={`${period.roleErn ?? ''}-${period.partnerId ?? ''}-${period.validFrom ?? ''}`}
                data-role={period.roleErn}
              >
                <TableCell>{roleNameOf(period.roleErn ?? '', rules)}</TableCell>
                <TableCell>
                  <Box component="code" sx={{ fontFamily: 'monospace' }}>
                    {period.partnerId}
                  </Box>
                </TableCell>
                <TableCell>
                  {t('policies.timeline.period', {
                    from: civil(period.validFrom, '—'),
                    to: civil(period.validTo, t('policies.timeline.open')),
                  })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}
